<template>
  <VDialog
    :model-value="show"
    width="560"
    persistent
    @update:model-value="$emit('update:show', $event)"
    data-testid="dialog-close-case"
  >
    <VCard class="rounded-lg !p-6 bg-white">
      <div class="flex justify-between items-start mb-4">
        <span class="text-h6 font-bold text-[#172B4D]">
          {{ t('Close case permanently') }}
        </span>
        <VBtn
          icon="close"
          variant="text"
          density="compact"
          size="small"
          class="text-secondary-text -mt-1 -mr-2"
          @click="close"
          data-testid="btn-close-icon"
        />
      </div>

      <div class="text-body-1 text-[#5E6C84] mb-6">
        <p class="mb-4">
          {{ t('Do you want to close this case permanently?') }}
        </p>

        <p class="mb-4">
          {{ t('Please note:') }}
        </p>

        <ul class="list-disc pl-5 space-y-3 mb-6">
          <li>{{ t('The patient will be removed from your Patient Overview.') }}</li>
          <li>{{ t('You cannot reopen the case.') }}</li>
          <li>{{ t('You cannot access unsaved consultation reports.') }}</li>
          <li>{{ t('You can create a new case for this patient after closing this case.') }}</li>
        </ul>
      </div>

      <div class="mb-8">
        <VSelect
          v-model="selectedReason"
          :items="reasonOptions"
          item-title="label"
          item-value="value"
          :label="t('Select reason')"
          variant="outlined"
          density="comfortable"
          hide-details
          color="primary"
          bg-color="white"
          class="rounded"
          data-testid="select-reason"
        />
      </div>

      <div class="flex justify-end gap-3">
        <VBtn
          variant="outlined"
          color="grey-darken-1"
          class="px-6 !normal-case !font-medium border-gray-300"
          height="40"
          @click="close"
          data-testid="btn-cancel"
        >
          {{ t('Cancel') }}
        </VBtn>

        <VBtn
          color="primary"
          variant="flat"
          class="px-6 !normal-case !font-bold"
          height="40"
          :disabled="!selectedReason"
          @click="confirm"
          data-testid="btn-confirm"
        >
          {{ t('Close case permanently') }}
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Discharge } from '@/generated'

defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'confirm', reason: Discharge): void
}>()

const { t } = useI18n()
const selectedReason = ref<Discharge | null>(null)

const reasonOptions = computed(() => [
  { value: Discharge.HOME, label: t('Home') },
  { value: Discharge.NORMAL_WARD, label: t('Normal Ward') },
  { value: Discharge.INTERMEDIATE_CARE, label: t('Intermediate Care') },
  { value: Discharge.OTHER_ICU, label: t('Other ICU') },
  { value: Discharge.DEATH, label: t('Death') }
])

function close() {
  selectedReason.value = null
  emit('update:show', false)
}

function confirm() {
  if (selectedReason.value) {
    emit('confirm', selectedReason.value)
    close()
  }
}
</script>

<style scoped>
:deep(.v-field__outline__start) {
  border-radius: 8px 0 0 8px !important;
}

:deep(.v-field__outline__end) {
  border-radius: 0 8px 8px 0 !important;
}
</style>
